import React, { SyntheticEvent, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { Autocomplete, Box, Button, DialogTitle, Grid, Stack, TextField, Tooltip } from "@mui/material";
import { Auth } from "aws-amplify";
import { DialogContent } from "@material-ui/core";
import { ArrowBack, DocumentScanner, Translate } from "@mui/icons-material";
import Actions from "../../store/actions";
import { getProductDataQuality, updateDataQuality } from "../../api/china";
import Store from "../../store";
import { qcStatuses } from "../PMDA/utils";
import IconButton from '@mui/material/IconButton';
import { isEmpty } from "lodash";

const EditTranslationQuality = () => {
  const { dispatch } = useContext<any>(Store);
  const { vinNumber } = useParams() as any;
  const history = useHistory();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [productData, setProductData] = useState<any>({});
  const [qcStatusDrugs, setQcStatusDrugs] = useState("");
  const [qcStatusExtracted, setQcStatusExtracted] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl,setPdfUrl] = useState({
    label:{
      s3_path:'',
      s3_path_original:'',
    },
    technical:{
      s3_path:'',
      s3_path_original:'',
    },
    current:'original',
    documentType:'label'
  });
  const pdfIframeRef = useRef<HTMLIFrameElement>(null);

  const fetchPageCuration = async () => {
    try {
      setLoading(true);
      const result = await getProductDataQuality(vinNumber);

      if (result.status !== 200 || !result?.data?.Success) {
        throw new Error("Error: Product Resource not available");
      }

      setProductData(result.data.Success);
      setQcStatusDrugs(result.data.Success?.qc_status?.drug?.status);
      setQcStatusExtracted(result.data.Success?.qc_status?.extracted?.status);
      setPdfUrl({
        label:result.data.Success?.label,
        technical:result.data.Success?.technical,
        current: 's3_path_original',
        documentType: 'label',
      });
    } catch (error) {
      await dispatch({
        type: Actions.SET_ALERT, value: { status: true, message: (error as Error).message }
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    try {
      setDisableSubmit(true);
      const user = await Auth.currentUserInfo();
      const prevQCStatusDrugs = productData?.qc_status?.drug?.status;
      const prevQCStatusExtracted = productData?.qc_status?.extracted?.status;
      const overallQc = {
        "drug": productData?.qc_status?.drug || {}, "extracted": productData?.qc_status?.extracted || {}
      };
      if (qcStatusDrugs !== prevQCStatusDrugs) {
        overallQc.drug = {
          status: qcStatusDrugs, curator_email: user.attributes.email, curator_name: user.attributes["custom:user"]
        };
      }
      if (qcStatusExtracted !== prevQCStatusExtracted) {
        overallQc.extracted = {
          status: qcStatusExtracted, curator_email: user.attributes.email, curator_name: user.attributes["custom:user"]
        };
      }
      const result = await updateDataQuality(vinNumber, {
        qc_status: overallQc, product_data: productData
      });
      if (result.status !== 200 || !result?.data?.Success) {
        throw new Error("Error: Translation Quality not updated");
      }
      await dispatch({
        type: Actions.SET_ALERT, value: { status: true, message: "Translation Quality Updated", color: "success" }
      });
      // refetch the data
      fetchPageCuration();
    } catch (error) {
      await dispatch({
        type: Actions.SET_ALERT, value: { status: true, message: (error as Error).message }
      });
    } finally {
      setDisableSubmit(false);
    }
  };
  // Helper function to update a specific marketed drug's extracted data.
  const handleMarketedDrugChange = (index: number, language: "translated" | "original", key: string, value: string) => {
    setProductData((prevData: any) => {
      // Create a shallow copy of the marketed_drugs array
      const updatedMarketedDrugs = [...prevData.marketed_drugs];
      // Clone the specific drug entry we want to update
      const currentDrug = { ...updatedMarketedDrugs[index] };
      // Ensure the extracted_data object and its nested language object exist
      const extracted_data = { ...(currentDrug.extracted_data || {}) };
      extracted_data[language] = { ...(extracted_data[language] || {}) };
      // Update the desired field with the new value
      extracted_data[language][key] = value;
      // Assign back to the current drug and update the overall array
      currentDrug.extracted_data = extracted_data;
      updatedMarketedDrugs[index] = currentDrug;
      return { ...prevData, marketed_drugs: updatedMarketedDrugs };
    });
  };
  const handlePrevious = () => {
    const vinFromLocalStorage = localStorage.getItem("chinaDataQualityVinList");
    if (!vinFromLocalStorage) {
      return;
    }
    const allVin = JSON.parse(vinFromLocalStorage);
    const currentIndex = allVin.indexOf(vinNumber);
    if (currentIndex === 0) {
      return;
    }
    const previousVin = allVin[currentIndex - 1];
    history.push(`/china/quality/translations/${previousVin}`);
  };
  const handleNext = () => {
    const vinFromLocalStorage = localStorage.getItem("chinaDataQualityVinList");
    if (!vinFromLocalStorage) {
      return;
    }
    const allVin = JSON.parse(vinFromLocalStorage);
    const currentIndex = allVin.indexOf(vinNumber);
    if (currentIndex === allVin.length - 1) {
      return;
    }
    const nextVin = allVin[currentIndex + 1];
    history.push(`/china/quality/translations/${nextVin}`);
  };

  // Implement the search functionality using the iframe's contentWindow
  const handleSearchPDF = (textToCopy: string) => {
    try {
      navigator.clipboard.writeText(textToCopy);
    } catch (error) {
      // navigator.clipboard.writeText() doesn't work on iOS,
      // so fall back to using the browser "copy" command.
      const textArea:any = document.createElement('textArea');
      textArea.value = textToCopy;

      document.body.appendChild(textArea);

      const range = document.createRange();
      range.selectNodeContents(textArea);

      const selection:any = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      textArea.setSelectionRange(0, 999999);

      document.execCommand('copy');

      document.body.removeChild(textArea);
    }
  };
  useEffect(() => {
    fetchPageCuration();
  }, [vinNumber]);

  return (<Box sx={{ overflow: "hidden", mt: 10 }}>
    <DialogTitle sx={{ m: 0, p: 2 }}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Button onClick={handlePrevious} fullWidth variant={'outlined'}>
          Previous
        </Button>
        <Typography
          component="p"
          variant="h5"
          style={{ color: "#3AB09E", fontWeight: "bold", fontSize: "28px", minWidth:300, textAlign:"center" }}>
          Translation Quality
        </Typography>
        <Button onClick={handleNext} fullWidth variant={'outlined'}>
          Next
        </Button>
      </Stack>
      <Tooltip title="Go Back" placement="right">
        <Button startIcon={<ArrowBack />} onClick={() => history.push("/china/quality/translation")}>
          Back to main page
        </Button>
      </Tooltip>

    </DialogTitle>
    {loading ? <Typography>Loading...</Typography> : <DialogContent>
      <Grid container spacing={2} display="flex">
        <Grid item xs={12} lg={7} sx={{ overflow: "auto", height: "70vh", padding: 2 }}>
          <Stack gap={2}>
            <Box>
              Drug name( trade name):
              <Stack direction={"row"} gap={1} mt={1}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label={"English"}
                  id={"trade_name"}
                  defaultValue={productData?.trade_name}
                  value={productData?.trade_name}
                  onChange={(e) => setProductData((prevData: any) => ({ ...prevData, trade_name: e.target.value }))}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled
                  label={"Chinese"}
                  id={"trade_name_original"}
                  defaultValue={productData?.trade_name_original}
                  value={productData?.trade_name_original}
                  onChange={(e) => setProductData((prevData: any) => ({
                    ...prevData, trade_name_original: e.target.value
                  }))}
                />
              </Stack>

            </Box>
            <Box>
              Generic name(active ingredient):
              <Stack direction={"row"} gap={1} mt={1}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label={"English"}
                  id={"generic_name"}
                  defaultValue={productData?.generic_name}
                  value={productData?.generic_name}
                  onChange={(e) => setProductData((prevData: any) => ({ ...prevData, generic_name: e.target.value }))}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled
                  label={"Chinese"}
                  id={"generic_name_original"}
                  defaultValue={productData?.generic_name_original}
                  value={productData?.generic_name_original}
                  onChange={(e) => setProductData((prevData: any) => ({
                    ...prevData, generic_name_original: e.target.value
                  }))}
                />
              </Stack>
            </Box>
            <Stack direction="row">
              <Autocomplete
                id="qc_status_product"
                fullWidth
                options={qcStatuses}
                value={qcStatusDrugs}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setQcStatusDrugs(newValue);
                  }
                }}
                sx={{ fontSize: "2vh" }}
                renderInput={params => (// eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField {...params} label="QC Status" variant="outlined" required />)}
              />
              <Button onClick={handleSubmit} disabled={disableSubmit} variant="contained" color="primary">
                Submit changes
              </Button>
            </Stack>

          </Stack>
          <Stack gap={2} mt={2}>
            <Typography variant="h6">Marketed Drugs</Typography>
            {productData?.marketed_drugs?.map((drug: any, index: number) => (
              <Stack sx={{ border: "1px solid #3AB09E", borderRadius: "5px", p: 2 }} key={index}>
                <Stack direction="row" justifyContent={"space-between"}>
                  <Typography variant="h6">{drug?.acceptance_number}</Typography>
                </Stack>
                <Box>
                  Generic name (active ingredient):
                  <Tooltip title={"Copy to clipboard"} placement={"top"}>
                    <Button variant="text" onClick={()=>handleSearchPDF('通用名称')}>通用名称</Button>
                  </Tooltip>
                  <Stack direction={"row"} gap={1} mt={1}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"English"}
                      id={"generic_name"}
                      defaultValue={drug?.extracted_data?.translated?.generic_name}
                      value={drug?.extracted_data?.translated?.generic_name || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "translated", "generic_name", e.target.value)}
                    />
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"Chinese"}
                      id={"通用名"}
                      defaultValue={drug?.extracted_data?.original?.["通用名"]}
                      value={drug?.extracted_data?.original?.["通用名"] || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "original", "通用名", e.target.value)}
                    />
                  </Stack>
                </Box>
                <Box>
                  Drug name (trade name / product name):
                  <Tooltip title={"Copy to clipboard"} placement={"top"}>
                  <Button variant="text" aria-label={"商品名称"} onClick={()=>handleSearchPDF('商品名称')}>商品名称</Button>
                  </Tooltip>
                  <Stack direction={"row"} gap={1} mt={1}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"English"}
                      id={"product_name"}
                      defaultValue={drug?.extracted_data?.translated?.product_name}
                      value={drug?.extracted_data?.translated?.product_name || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "translated", "product_name", e.target.value)}
                    />
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"Chinese"}
                      id={"商品名称"}
                      defaultValue={drug?.extracted_data?.original?.["商品名称"]}
                      value={drug?.extracted_data?.original?.["商品名称"] || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "original", "商品名称", e.target.value)}
                    />
                  </Stack>
                </Box>
                <Box>
                  English name:
                  <Tooltip title={"Copy to clipboard"} placement={"top"}>
                    <Button variant="text" aria-label={"英文名称"} onClick={()=>handleSearchPDF('英文名称')}>英文名称</Button>
                  </Tooltip>
                  <Stack direction={"row"} gap={1} mt={1}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"English"}
                      id={"english_name"}
                      defaultValue={drug?.extracted_data?.translated?.english_name}
                      value={drug?.extracted_data?.translated?.english_name || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "translated", "english_name", e.target.value)}
                    />
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"Chinese"}
                      id={"英文名称"}
                      defaultValue={drug?.extracted_data?.original?.["英文名称"]}
                      value={drug?.extracted_data?.original?.["英文名称"] || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "original", "英文名称", e.target.value)}
                    />
                  </Stack>
                </Box>
                <Box>
                  Chinese Pinyin:
                  <Tooltip title={"Copy to clipboard"} placement={"top"}>
                    <Button variant="text" aria-label="汉语拼音" onClick={()=>handleSearchPDF('汉语拼音')}>汉语拼音</Button>
                  </Tooltip>
                  <Stack direction={"row"} gap={1} mt={1}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"English"}
                      id={"chinese_pinyin"}
                      defaultValue={drug?.extracted_data?.translated?.chinese_pinyin}
                      value={drug?.extracted_data?.translated?.chinese_pinyin || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "translated", "chinese_pinyin", e.target.value)}
                    />
                    <TextField
                      variant="outlined"
                      fullWidth
                      label={"Chinese"}
                      id={"汉语拼音"}
                      defaultValue={drug?.extracted_data?.original?.["汉语拼音"]}
                      value={drug?.extracted_data?.original?.["汉语拼音"] || ""}
                      onChange={(e) => handleMarketedDrugChange(index, "original", "汉语拼音", e.target.value)}
                    />
                  </Stack>
                </Box>
              </Stack>))}
            <Stack direction="row">
              <Autocomplete
                id="qc_status_product"
                fullWidth
                options={qcStatuses}
                value={qcStatusExtracted}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setQcStatusExtracted(newValue);
                  }
                }}
                sx={{ fontSize: "2vh" }}
                renderInput={params => (// eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField {...params} label="QC Status" variant="outlined" required />)}
              />
              <Button onClick={handleSubmit} disabled={disableSubmit} variant="contained" color="primary">
                Submit extracted data changes
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={5} sx={{ height: "70vh" }}>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
            <IconButton onClick={()=>{
              setPdfUrl((prev)=>({
                ...prev,
                current: prev.current === 's3_path_original' ? 's3_path' : 's3_path_original'
              }))
            }}>
              <Translate/>
            </IconButton>
            {
              !isEmpty(pdfUrl.technical) && <IconButton onClick={()=>{
                setPdfUrl((prev)=>({
                  ...prev,
                  documentType: prev.documentType === 'label' ? 'technical' : 'label'
                }))
              }}>
                <DocumentScanner/>
              </IconButton>
            }

          </Stack>

          <iframe
            ref={pdfIframeRef}
            //@ts-ignore
            src={pdfUrl[pdfUrl.documentType][pdfUrl?.current] ?? ''}
            title="Product Resource"
            style={{ height: "100%", width: "100%" }}
          />
        </Grid>
      </Grid>
    </DialogContent>

    }
  </Box>);
};
export default EditTranslationQuality;
